// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/bulk-orders — B2B Bulk Restaurant & Party Feast order management)
// End-to-end staff capture of restaurant/hotel supply accounts and party feast orders that come in by phone, WhatsApp, e-mail or a walk-in — there
// is no customer-facing bulk-order flow, so every one of these is typed in by staff (compare pages/admin/AdminOrders, which lists real customer-paid
// orders). KPI cards + status pills come from GET /admin/bulk-orders/summary (real counts and rupee figures, no invented targets). "New bulk order"
// opens a form: account/contact, phone/email, an optional GSTIN (checksum-validated, never verified against the GST portal — there is no
// integration), PO reference, how the order came in, line items (staff types the price — bulk/negotiated rates legitimately differ from the retail
// catalog), delivery address and payment terms in the staff's own words. Row actions open the detail page (AdminBulkOrderDetail) for status,
// driver hand-off (the real fleet, `utils/fleetApi`) and payment marking. Viewers get the same screen read-only.
// Coming soon (dimmed, sample-free): GST e-invoicing/IRN sync, an automated corporate credit-line ledger — see the detail page.
// Neighbours: pages/admin/AdminBulkOrderDetail, utils/bulkOrders, components/admin/Modal, components/admin/AdminParts.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Loader2, Plus, Search, Trash2 } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import Modal from "../../components/admin/Modal";
import { SoonPanel } from "../../components/admin/AdminParts";
import { ApiError } from "../../utils/api";
import { formatWhen } from "../../utils/orders";
import {
  BULK_STATUS_PILL,
  CHANNELS,
  CHANNEL_LABEL,
  STATUS_LABEL,
  canWriteBulkOrders,
  createBulkOrder,
  fetchBulkOrderSummary,
  fetchBulkOrders,
  inr,
  type BulkLineInput,
  type BulkOrder,
  type BulkOrderStatus,
  type BulkOrderSummary,
  type Channel,
} from "../../utils/bulkOrders";

const PILLS: { id: "all" | BulkOrderStatus; label: string }[] = [
  { id: "all", label: "All" },
  { id: "requested", label: "Requested" },
  { id: "confirmed", label: "Confirmed" },
  { id: "preparing", label: "Preparing" },
  { id: "dispatched", label: "Dispatched" },
  { id: "delivered", label: "Delivered" },
  { id: "cancelled", label: "Cancelled" },
];
const emptyLine = (): BulkLineInput => ({ description: "", weightKg: undefined, amountInr: 0 });

function Kpi({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
      <p className="text-xs font-semibold text-ocean-800/80 dark:text-foam-100/75">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold">{value}</p>
      <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">{hint}</p>
    </div>
  );
}

/** The "New bulk order" form: account/contact, an optional GSTIN, line items staff price themselves, delivery and payment terms. */
function CreateForm({ onDone, onClose }: { onDone: (order: BulkOrder) => void; onClose: () => void }) {
  const [accountName, setAccountName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [gstin, setGstin] = useState("");
  const [poReference, setPoReference] = useState("");
  const [channel, setChannel] = useState<Channel>("phone");
  const [lines, setLines] = useState<BulkLineInput[]>([emptyLine()]);
  const [addressLine, setAddressLine] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subtotal = lines.reduce((sum, line) => sum + (Number.isFinite(line.amountInr) ? line.amountInr : 0), 0);
  const total = subtotal + deliveryFee;
  const validLines = lines.filter((l) => l.description.trim() && l.amountInr > 0);

  const setLine = (index: number, patch: Partial<BulkLineInput>) =>
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const submit = async () => {
    setError(null);
    if (!accountName.trim() || !phone.trim() || validLines.length === 0 || !addressLine.trim() || !city.trim() || !/^[0-9]{6}$/.test(pincode)) {
      setError("Fill in the account, phone, at least one priced line item, and a full delivery address (6-digit pincode).");
      return;
    }
    setBusy(true);
    try {
      const order = await createBulkOrder({
        accountName: accountName.trim(),
        contactName: contactName.trim() || undefined,
        phone: phone.trim(),
        email: email.trim() || undefined,
        gstin: gstin.trim() || undefined,
        poReference: poReference.trim() || undefined,
        channel,
        items: validLines,
        subtotal,
        deliveryFee,
        total,
        delivery: { addressLine: addressLine.trim(), city: city.trim(), pincode, notes: notes.trim() || undefined },
        paymentTerms: paymentTerms.trim(),
      });
      onDone(order);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save that bulk order.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm font-semibold">Account / restaurant name*<input value={accountName} onChange={(e) => setAccountName(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">Contact person<input value={contactName} onChange={(e) => setContactName(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">Phone*<input value={phone} onChange={(e) => setPhone(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">GSTIN (optional)<input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="15 characters" className="min-h-11 rounded-lg bg-ocean-700/5 px-3 font-mono dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">PO reference<input value={poReference} onChange={(e) => setPoReference(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">How it came in
          <select value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10">
            {CHANNELS.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
          </select>
        </label>
      </div>

      <fieldset className="rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
        <legend className="px-1 text-sm font-bold">Line items — price is what you're quoting this account, not the retail catalog rate*</legend>
        <div className="mt-2 flex flex-col gap-2">
          {lines.map((line, index) => (
            <div key={index} className="flex flex-wrap items-end gap-2">
              <label className="flex flex-1 flex-col gap-1 text-xs font-semibold">Description<input value={line.description} onChange={(e) => setLine(index, { description: e.target.value })} placeholder="e.g. 60 kg Surmai, whole cleaned" className="min-h-11 min-w-[220px] rounded-lg bg-white px-3 text-sm dark:bg-ocean-900" /></label>
              <label className="flex w-24 flex-col gap-1 text-xs font-semibold">Weight (kg)<input type="number" min={0} value={line.weightKg ?? ""} onChange={(e) => setLine(index, { weightKg: e.target.value ? Number(e.target.value) : undefined })} className="min-h-11 rounded-lg bg-white px-3 text-sm dark:bg-ocean-900" /></label>
              <label className="flex w-28 flex-col gap-1 text-xs font-semibold">Amount (₹)<input type="number" min={0} value={line.amountInr || ""} onChange={(e) => setLine(index, { amountInr: Number(e.target.value) || 0 })} className="min-h-11 rounded-lg bg-white px-3 text-sm dark:bg-ocean-900" /></label>
              <button type="button" onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))} disabled={lines.length <= 1} aria-label={`Remove line ${index + 1}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-coral-500/10 text-coral-700 disabled:opacity-40 dark:text-coral-300"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} className="mt-2 inline-flex min-h-11 items-center gap-1 rounded-lg bg-white px-3 text-sm font-bold dark:bg-ocean-900"><Plus className="h-4 w-4" aria-hidden="true" /> Add line</button>
      </fieldset>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm font-semibold sm:col-span-2">Delivery address*<input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">City*<input value={city} onChange={(e) => setCity(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">Pincode*<input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold sm:col-span-2">Delivery notes<input value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold">Delivery fee (₹)<input type="number" min={0} value={deliveryFee || ""} onChange={(e) => setDeliveryFee(Number(e.target.value) || 0)} className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
        <label className="flex flex-col gap-1 text-sm font-semibold sm:col-span-2">Payment terms, in your own words<input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder='e.g. "Cash on delivery" or "Invoice, pay in 7 days"' className="min-h-11 rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10" /></label>
      </div>

      <p className="text-sm font-bold">Subtotal {inr(subtotal)} + delivery {inr(deliveryFee)} = <span className="text-base">{inr(total)}</span></p>
      <p role="alert" className="min-h-5 text-sm text-coral-600 dark:text-coral-300">{error}</p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="min-h-11 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10">Cancel</button>
        <button type="button" disabled={busy} onClick={() => void submit()} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-500 px-4 text-sm font-bold text-ocean-950 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}Save bulk order</button>
      </div>
    </div>
  );
}

export default function AdminBulkOrders() {
  const auth = useAuth();
  const canWrite = canWriteBulkOrders(auth.user?.role);
  const [orders, setOrders] = useState<BulkOrder[] | null>(null);
  const [summary, setSummary] = useState<BulkOrderSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState<"all" | BulkOrderStatus>("all");
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [list, kpis] = await Promise.all([fetchBulkOrders(), fetchBulkOrderSummary()]);
      setOrders(list);
      setSummary(kpis);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(PILLS.filter((p) => p.id !== "all").map((p) => [p.id, 0])) as Record<BulkOrderStatus, number>;
    for (const order of orders ?? []) byStatus[order.status] += 1;
    return byStatus;
  }, [orders]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (orders ?? []).filter((order) => {
      if (status !== "all" && order.status !== status) return false;
      if (!needle) return true;
      return order.ref.toLowerCase().includes(needle) || order.accountName.toLowerCase().includes(needle) || order.phone.includes(needle);
    });
  }, [orders, status, query]);

  return (
    <div className="flex flex-col gap-4 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ocean-800/80 dark:text-foam-100/75">
            <Link to="/admin" className="underline">Command Ops</Link><span aria-hidden="true">/</span>B2B &amp; bulk orders
          </p>
          <h1 className="font-display text-2xl font-bold">Bulk restaurant &amp; party feast orders</h1>
          <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Every restaurant, hotel and party-feast order staff have logged from a call, WhatsApp message, e-mail or walk-in — captured end to end, from account details to delivery and payment.</p>
        </div>
        {canWrite && (
          <button type="button" onClick={() => setShowCreate(true)} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-500 px-4 text-sm font-bold text-ocean-950"><Plus className="h-4 w-4" aria-hidden="true" /> New bulk order</button>
        )}
      </div>

      <section aria-label="Bulk order figures" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total on file" value={summary?.totalOrders ?? "—"} hint="all time" />
        <Kpi label="Active" value={summary?.activeOrders ?? "—"} hint="not delivered/cancelled" />
        <Kpi label="Awaiting prep" value={summary?.awaitingPrep ?? "—"} hint="requested + confirmed" />
        <Kpi label="Outstanding" value={summary ? inr(summary.outstandingInr) : "—"} hint="marked unpaid, not cancelled" />
      </section>
      <p className="text-sm">Weight on active orders (weighed lines only): <strong>{summary ? `${summary.weightKgActive} kg` : "—"}</strong></p>

      <section aria-label="Filters" className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:w-96">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 opacity-60" aria-hidden="true" />
            <label htmlFor="bulk-search" className="sr-only">Search bulk orders</label>
            <input id="bulk-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ref, account name or phone" className="min-h-11 w-full rounded-xl bg-ocean-700/5 pl-10 pr-3 text-sm dark:bg-white/10" />
          </div>
          <div role="group" aria-label="Bulk order status" className="flex flex-wrap gap-1.5">
            {PILLS.map((pill) => {
              const n = pill.id === "all" ? orders?.length ?? 0 : counts[pill.id];
              return (
                <button key={pill.id} type="button" aria-pressed={status === pill.id} onClick={() => setStatus(pill.id)} className={`min-h-11 rounded-full px-4 text-sm font-bold ${status === pill.id ? "bg-ocean-800 text-white" : "bg-ocean-700/10 dark:bg-white/10"}`}>{pill.label} ({n})</button>
              );
            })}
          </div>
        </div>
      </section>

      <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
      {failed && <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">We couldn&apos;t load bulk orders.</p>}
      {!orders && !failed && <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>}
      {orders && filtered.length === 0 && <p className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">{orders.length === 0 ? "No bulk orders logged yet." : "No bulk orders match these filters."}</p>}

      {filtered.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left text-sm">
              <caption className="sr-only">Bulk orders, newest first</caption>
              <thead className="bg-ocean-700/5 text-xs uppercase tracking-wider dark:bg-white/5">
                <tr>
                  <th scope="col" className="px-3 py-3">Ref</th>
                  <th scope="col" className="px-3 py-3">Account</th>
                  <th scope="col" className="px-3 py-3">Channel</th>
                  <th scope="col" className="px-3 py-3">Logged</th>
                  <th scope="col" className="px-3 py-3">Status</th>
                  <th scope="col" className="px-3 py-3">Total</th>
                  <th scope="col" className="py-3 pl-3 pr-4 text-right">Open</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => (
                  <tr key={order.ref} data-testid="bulk-order-row" className="border-t border-ocean-900/5 align-top dark:border-white/10">
                    <td className="px-3 py-3.5"><Link to={`/admin/bulk-orders/${order.ref}`} className="whitespace-nowrap font-display font-bold underline">{order.ref}</Link></td>
                    <td className="px-3 py-3.5"><p className="font-bold">{order.accountName}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{order.phone}</p></td>
                    <td className="px-3 py-3.5">{CHANNEL_LABEL[order.channel]}</td>
                    <td className="px-3 py-3.5">{formatWhen(order.createdAt)}</td>
                    <td className="px-3 py-3.5"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${BULK_STATUS_PILL[order.status]}`}>{STATUS_LABEL[order.status]}</span><p className="mt-1 text-xs text-ocean-800/80 dark:text-foam-100/75">{order.paymentStatus === "paid" ? "Paid" : "Unpaid"}</p></td>
                    <td className="px-3 py-3.5"><p className="font-bold">{inr(order.total)}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{order.totalWeightKg > 0 ? `${order.totalWeightKg} kg` : "—"}</p></td>
                    <td className="py-3.5 pl-3 pr-4 text-right"><Link to={`/admin/bulk-orders/${order.ref}`} title="Open details" className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-ocean-700/10 dark:bg-white/10"><Eye className="h-4 w-4" aria-hidden="true" /><span className="sr-only">Open bulk order {order.ref}</span></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SoonPanel title="Corporate credit-line ledger" subtitle="An automated running balance per account with billing cycles — this needs a business decision on credit terms; today's `paymentTerms`/`paymentStatus` are just what staff typed.">
        <p className="text-sm">Coastal Kitchen · Credit limit — · Balance — · Next statement —</p>
      </SoonPanel>

      {showCreate && (
        <Modal title="New bulk order" onClose={() => setShowCreate(false)} wide>
          <CreateForm onClose={() => setShowCreate(false)} onDone={(order) => { setShowCreate(false); setNotice(`Bulk order ${order.ref} saved.`); void load(); }} />
        </Modal>
      )}
    </div>
  );
}
